(ns manage.content-variant-page.table.db
  (:require [camel-snake-kebab.core :as csk]
            [clojure.set :as set]
            [clojure.string :as str]
            [common.db.table.backend :refer [map->RemoteScrollingBackend]]
            [common.db.table.core :refer [register-table]]
            [mate.core :as mc]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as r.easy]))

(def backend
  (map->RemoteScrollingBackend
   {:on-init [:resource.content-variants/fetch-all]
    :on-change [:resource.content-variants/fetch-all]}))

(register-table
 {:namespace :table.content-variants
  :initial-page {:size 100}
  :value-sub [:table.content-variants/data]
  :backend backend})

(def entity-name "Content variant")

(def ui-messages
  {:download {:failure "Failed to download variant renders"}})

(defn derive-variant-status [{:keys [status]}]
  (keyword status))

;; derive-variant-status2 differs from derive-variant-status which was built without
;; the assumption that being a default variant would mean something different in terms
;; of which action can be performed on it.
(defn- derive-variant-status2 [{:keys [default-variant status]}]
  (cond
    default-variant :default-variant ; <- this is the only difference with function derive-variant-status
    :else (keyword status)))

(defn derive-render-status [{:keys [renders]}]
  (let [job-states (mapv :job-state renders)]
    (cond
      (some #{"failed"} job-states)
      :failed

      (some #{"running"} job-states)
      :rendering

      (some #{"leased"} job-states)
      :render-starting

      (some #{"created"} job-states)
      :created

      (some #{"ready"} job-states)
      :queued

      (empty? renders)
      :unknown

      (every? #{"succeeded"} job-states)
      :succeeded

      :else
      :unknown)))

(def ^{:doc
       "To enable filtering on JSONB columns, the client needs to send the name of a 'resolver' to the backend.
       The resolvers are setup in the backend, and they tell the backend how to search for a 'name' and a 'value'
       in a JSONB column, and how to apply the different operators.

       The names of the resolvers are sent in the metadata of the response, when fetching variants."}
  class->filter-resolver (let [meta-resolver "ad-content-values"
                               tiktok-resolver "ad-content-values"]
                           {"content-key" "content-values"
                            "targeting-key" "targeting-values"
                            "render-status" "render-status"
                            "variant-status" "variant-status"
                            "identifier-key" "content-id"
                            "click-through-key" "click-through"
                            "meta-cta-key" meta-resolver
                            "meta-cta-caption-key" meta-resolver
                            "meta-description-key" meta-resolver
                            "meta-headline-key" meta-resolver
                            "meta-primary-text-key" meta-resolver
                            "tiktok-ad-name-key" tiktok-resolver
                            "tiktok-ad-text-key" tiktok-resolver
                            "tiktok-cta-key" tiktok-resolver}))

(def render-none-str "none")
(def render-failed-str "failed")
(def render-succeeded-str "succeeded")
(def render-queued-str "queued")
(def render-starting-str "render starting")
(def render-rendering-str "rendering")

(def pretty-value->value
  ^{:private true
    :doc
    "Maps a 'pretty' string that is used in the UI, to a value that is expected by the backend to apply filters.
    See the API definition of content-build."}
  {render-starting-str "render-starting"})

(defn- on-filter-model-changed* [class->filter-resolver columns grid-filter-model]
  ;; TODO WPPODC-2442 make reusable
  ;; TODO Check if common.queries.muix-filter-util/get-filter-params can be used here
  (let [field->class (mc/index-by :field :class columns)
        filters {:link-operator (:logicOperator grid-filter-model)
                 :filters (->> (:items grid-filter-model)
                               (mapv (fn [filter]
                                       (let [resolver (some->> (:field filter)
                                                               (get field->class)
                                                               (get class->filter-resolver))]
                                         (cond-> {:operator (csk/->kebab-case (:operator filter))
                                                  :column (:field filter)
                                                  :value (or (pretty-value->value (:value filter))
                                                             (:value filter))}
                                           resolver (assoc :resolver resolver)))))
                               (filter (fn [filter]
                                         (or (seq (:value filter))
                                             (#{"is-empty" "is-not-empty"} (:operator filter))))))}]
    (when (seq (:filters filters))
      filters)))

(defn on-filter-model-changed [columns ^js grid-filter-model ^js _grid-callback-details]
  (let [filters (on-filter-model-changed* class->filter-resolver
                                          columns
                                          (js->clj grid-filter-model :keywordize-keys true))]
    (rf/dispatch [:table.content-variants/filters filters])
    (rf/dispatch [:resource.content-variants/get-states filters])))

(defn status->allowed-actions [status]
  (case status
    :default-variant []

    :draft [:approve :approve-publish]

    (:approved :unpublished) [:publish :unapprove]

    :published [:unpublish :unpublish-unapprove]

    []))

(defn on-sorting-model-changed
  [_columns field _direction]
  (rf/dispatch [:table.content-variants/sort-column field]))

(defn convert-status-to-action-keys [statuses]
  (let [exceptions {}]
    (set/difference (set (mapcat status->allowed-actions statuses))
                    (set (mapcat exceptions statuses)))))

(defn is-action-allowed-for-status? [action status]
  (let [possible-actions (convert-status-to-action-keys [status])]
    (boolean (possible-actions action))))

(rf/reg-sub :table.content-variants/selected-rows-allowed-actions
  :<- [:table.content-variants/selected-row-ids->rows]
  (fn [selected-rows _]
    (-> (map derive-variant-status2 selected-rows)
        (convert-status-to-action-keys))))

(rf/reg-sub :table.content-variants/selected-rows-allowed-render-actions
  :<- [:table.content-variants/selected-row-ids->rows]
  (fn [selected-rows _]
    (set (map derive-render-status selected-rows))))

(rf/reg-sub :table.content-variants/selected-row-ids->rows
  :<- [:table.content-variants/selected-rows]
  :<- [:table.content-variants/value]
  (fn [[selected-row-ids {rows :value}]]
    (filterv #(contains? selected-row-ids (:id %)) rows)))

(def update-status-messages
  {:approve {:failure "There was a problem marking the selected variants as approved"}
   :approve-bulk {:failure "There was a problem marking the variants as approved"}

   :unapprove {:failure "There was a problem marking the selected variants as unapproved"}
   :unapprove-bulk {:failure "There was a problem marking the variants as unapproved"}

   :publish {:failure "There was a problem publishing the selected variants"}
   :publish-bulk {:failure "There was a problem publishing the variants"}

   :unpublish {:failure "There was a problem unpublishing the selected variants"}
   :unpublish-bulk {:failure "There was a problem unpublishing the variants"}})

(def update-variant-status-events
  {:approve {:first :resource.content-variants/approve-many
             :success :resource.content-variants/approve-many-success
             :failure :resource.content-variants/approve-many-failure}
   :unapprove {:first :resource.content-variants/unapprove-many
               :success :resource.content-variants/unapprove-many-success
               :failure :resource.content-variants/unapprove-many-failure}
   :publish {:first :resource.content-variants/publish-many
             :success :resource.content-variants/publish-many-success
             :failure :resource.content-variants/publish-many-failure}
   :unpublish {:first :resource.content-variants/unpublish-many
               :success :resource.content-variants/unpublish-many-success
               :failure :resource.content-variants/unpublish-many-failure}
   :approve-publish {:first :resource.content-variants/approve-then-publish-many
                     :actions [:approve :publish]}
   :unpublish-unapprove {:first :resource.content-variants/unpublish-then-unapprove-many
                         :actions [:unpublish :unapprove]}

   :approve-bulk {:first :resource.content-variants/approve-by-feed
                  :success :resource.content-variants/approve-many-success
                  :failure :resource.content-variants/approve-many-failure}
   :unapprove-bulk {:first :resource.content-variants/unapprove-by-feed
                    :success :resource.content-variants/unapprove-many-success
                    :failure :resource.content-variants/unapprove-many-failure}
   :publish-bulk {:first :resource.content-variants/publish-by-feed
                  :success :resource.content-variants/publish-many-success
                  :failure :resource.content-variants/publish-many-failure}
   :unpublish-bulk {:first :resource.content-variants/unpublish-by-feed
                    :success :resource.content-variants/unpublish-many-success
                    :failure :resource.content-variants/unpublish-many-failure}

   :default {:first :resource.content-variants/set-default
             :success :resource.content-variants/set-default-success
             :failure :resource.content-variants/set-default-failure}})

(defn- update-variant-flow-rules [action]
  [{:when :seen?
    :events (get-in update-variant-status-events [action :success])
    :dispatch-fn (fn [[_event response]]
                   (let [updated-variant-ids response]
                     (if (pos? (count updated-variant-ids))
                       [[:table.content-variants/update-variant-status-many-success action updated-variant-ids]]
                       [[:table.content-variants/update-variant-status-many-failure action]])))}
   {:when :seen?
    :events (get-in update-variant-status-events [action :failure])
    :halt? true
    :dispatch-fn (fn [[_event response]]
                   [[:table.content-variants/update-variant-status-many-failure action response]])}])

(rf/reg-event-fx :table.content-variants/update-variant-statuses-bulk
  [(rf/inject-cofx :sub :table.content-variants/filters)]
  (fn [{:table.content-variants/keys [filters]} [_ action]]
    {:fx [[:async-flow
           {:id :table.content-variants/update-variant-status-bulk-flow
            :first-dispatch [(get-in update-variant-status-events [action :first]) filters]
            :rules (update-variant-flow-rules action)}]]}))

(rf/reg-event-fx :table.content-variants/update-variant-statuses
  [(rf/inject-cofx :sub [:resource.adset/current-channel])
   (rf/inject-cofx :sub (fn [[_ _ variant-ids]]
                          [:table.content-variants/succeeded-renders variant-ids]))]
  (fn [{adset-channel :resource.adset/current-channel
        :table.content-variants/keys [succeeded-renders]}
       [_ action variant-ids]]
    (let [rule-actions (get-in update-variant-status-events [action :actions] [action])]
      {:fx [(if (and (not= adset-channel "display")
                     (not= (set (keys succeeded-renders)) (set variant-ids))
                     (contains? #{:approve :publish :approve-publish} action))
              [:dispatch [:modal.content-variants/open-renders-modal {:variant-ids variant-ids :action action}]]
              [:async-flow
               {:id :table.content-variants/update-variant-many-status-flow
                :first-dispatch [(get-in update-variant-status-events [action :first]) variant-ids]
                :rules (mapcat (fn [action]
                                 (update-variant-flow-rules action))
                               rule-actions)}])]})))

(rf/reg-event-fx :table.content-variants/update-selected-variant-statuses
  [(rf/inject-cofx :sub :table.content-variants/selected-row-ids->rows)]
  (fn [{:table.content-variants/keys [selected-row-ids->rows]}
       [_ action]]
    (let [rows-to-update (vec (keep (fn [row]
                                      (let [status (derive-variant-status2 row)]
                                        (when (is-action-allowed-for-status? action status)
                                          (:id row))))
                                    selected-row-ids->rows))]
      {:dispatch [:table.content-variants/update-variant-statuses action rows-to-update]})))

(rf/reg-event-fx :table.content-variants/update-variant-status-many-success
  [(rf/inject-cofx :sub :table.content-variants/filters)]
  (fn [{:table.content-variants/keys [filters]} [_event action variant-ids]]
    (letfn [(action->verb [action]
              (case action
                (:approve :approve-bulk) "approved"
                (:unapprove :unapprove-bulk) "unapproved"
                (:publish :publish-bulk) "published"
                (:unpublish :unpublish-bulk) "unpublished"))]
      {:fx [[:dispatch [:resource.content-variants/get-states filters]]
            [:dispatch [:response-handler/handle-response
                        {:entity-name entity-name
                         :type :success
                         :message (str/join " " ["Successfully" (action->verb action) (count variant-ids) "variants"])}]]]})))

(rf/reg-event-fx :table.content-variants/update-variant-status-many-failure
  (fn [_ [event action response]]
    (when (not= action :default)
      ;; error handler for set-default call is handled separately
      {:fx [[:dispatch [:response-handler/handle-response
                        {:entity-name entity-name
                         :type :error
                         :message (get-in update-status-messages [action :failure])
                         :event event
                         :response response}]]]})))

(rf/reg-event-fx :table.content-variants/trigger-render-variants
  [(rf/inject-cofx :sub :table.content-variants/selected-row-ids->rows)]
  (fn [{:table.content-variants/keys [selected-row-ids->rows]}
       [_ action]]
    (let [row-ids (mapv :id selected-row-ids->rows)]
      {:dispatch [:resource.content-variants/trigger-render-many row-ids action]})))

(rf/reg-event-fx :table.content-variants/download-variant-renders
  (fn [_ [_ variant-id]]
    {:fx [[:async-flow
           {:id :table.content-variants/download-variant-renders-flow
            :first-dispatch [:resource.content-variants/get-variant-renders variant-id]
            :rules [{:when :seen?
                     :events :resource.content-variants/get-variant-renders-success
                     :halt? true
                     :dispatch [:table.content-variants/download-variant-renders-success variant-id]}
                    {:when :seen?
                     :events :resource.content-variants/get-variant-renders-failure
                     :halt? true
                     :dispatch [:table.content-variants/download-variant-renders-failure]}]}]]}))

(rf/reg-event-fx :table.content-variants/download-variant-renders-success
  [(rf/inject-cofx :sub (fn [[_ variant-id]]
                          [:table.content-variants/succeeded-renders variant-id]))]
  (fn [{:table.content-variants/keys [succeeded-renders]} [_ variant-id]]
    (if (empty? succeeded-renders)
      {:fx [[:dispatch [:modal.content-variants/open-renders-modal {:variant-ids [variant-id] :action :download}]]]}
      {:fx (doall (for [[_ renders] succeeded-renders
                        render renders
                        :let [url (get-in render [:job-result :url])]
                        :when url]
                    [:router/open-in-tab url]))})))

(rf/reg-event-fx :table.content-variants/download-variant-renders-failure
  (fn [_ [event]]
    {:fx [[:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :error
                       :message (get-in ui-messages [:download :failure])
                       :event event}]]]}))

(rf/reg-sub :table.content-variants/preview-route-params
  :<- [:resource.adset/preview-token]
  :<- [:resource.adset/current-feed-id]
  :<- [:url-context]
  :<- [:table.content-variants/value]
  (fn [[preview-token current-feed-id url-context variants]]
    (let [cursor (-> variants
                     :value
                     first
                     :cursor)]
      [:adset/content-variants-preview
       (merge url-context {:preview-token (:value preview-token)})
       {:cursor cursor
        :feed-id current-feed-id}])))

(rf/reg-sub :table.content-variants/preview-route-url
  :<- [:table.content-variants/preview-route-params]
  (fn [[route params query-params] [_ cursor]]
    (r.easy/href route params (cond-> query-params
                                cursor (assoc :cursor cursor)))))

(rf/reg-event-fx :table.content-variants/preview
  [(rf/inject-cofx :sub :table.content-variants/preview-route-params)
   (rf/inject-cofx :sub (fn [[_ {:keys [variant-id]}]]
                          [:table.content-variants/succeeded-renders variant-id]))]
  (fn [{:table.content-variants/keys [succeeded-renders preview-route-params]}
       [_ {:keys [cursor variant-id]}]]
    (let [[route params query-params] preview-route-params
          query-params (cond-> query-params
                         cursor (assoc :cursor cursor))]
      {:fx [(if (empty? succeeded-renders)
              [:dispatch [:modal.content-variants/open-renders-modal {:variant-ids [variant-id] :action :preview}]]
              [:router/navigate! [route {:params params :query query-params}]])]})))

(rf/reg-sub :table.content-variants/data
  :<- [:resource.feed-imports/field-classes-sorted]
  :<- [:resource.content-variants/all]
  (fn [[column-name-classes {:keys [state value]}]]
    (let [;; TODO: Remove the following conversion once WPPODC-5214 is in prod.
          {click-tracker-column-names "third-party-click-tracker-key"
           impression-tracker-column-names "third-party-impression-tracker-key"} (mc/group-by :class :name column-name-classes)
          convert-strings-to-name-values (fn [values column-names]
                                           (->> values
                                                (into [] (map-indexed (fn [index value]
                                                                        (if (string? value)
                                                                          {:name (get column-names index)
                                                                           :value value}
                                                                          value))))))

          value* (map (fn [{{:keys [content-values
                                    targeting-values
                                    optimizing-values
                                    click-through
                                    third-party-click-trackers
                                    third-party-impression-trackers] :as row} :data
                            cursor :cursor}]
                        (assoc row
                               :cursor cursor
                               :values (->> (concat content-values
                                                    targeting-values
                                                    optimizing-values
                                                    click-through
                                                    (convert-strings-to-name-values third-party-click-trackers click-tracker-column-names)
                                                    (convert-strings-to-name-values third-party-impression-trackers impression-tracker-column-names))
                                            (into {} (map (fn [{:keys [name value]}]
                                                            [(keyword name) value]))))))
                      (:data value))]
      {:state state :value value*})))

(rf/reg-sub :table.content-variants/columns
  :<- [:resource.feed-imports/field-classes-sorted]
  :<- [:resource.feed-importers/field-content-mappings]
  (fn [[field-classes field-content-mappings]]
    (let [field-content-mappings-by-name (group-by :name field-content-mappings)
          field-class->content-mapping #(first (get field-content-mappings-by-name (:name %)))]
      (for [fc field-classes]
        (merge fc (field-class->content-mapping fc))))))

(rf/reg-sub :table.content-variants/columns-by-class
  :<- [:table.content-variants/columns]
  (fn [columns [_ class]]
    (filter #(= (:class %) class) columns)))

(rf/reg-sub :table.content-variants/renders
  :<- [:resource.content-variants/all]
  (fn [all [_ variant-ids]]
    (let [variant-ids-vec (if-not (vector? variant-ids) [variant-ids] variant-ids)]
      (-> (->> (get-in all [:value :data])
               (map (juxt #(get-in % [:data :id]) #(get-in % [:data :renders])))
               (into {}))
          (select-keys variant-ids-vec)))))

(rf/reg-sub :table.content-variants/renders-overall-progress ; 0 to 100
  :<- [:resource.content-variants/all]
  (fn [all-variants-data _]
    (let [variants (-> all-variants-data :value :data
                       (->> (mapv :data)))
          renders (mapcat :renders variants)
          completed-renders-count (-> (keep (comp #{"succeeded" "failed"} :job-state) renders)
                                      count)
          renders-count (count renders)]
      (if (pos? renders-count)
        (-> (* completed-renders-count 100)
            (/ renders-count)
            int)
        0))))

(rf/reg-sub :table.content-variants/renders-overall-status ; [n-items status]
  :<- [:resource.content-variants/all]
  (fn [all-variants-data _]
    (let [variants (-> all-variants-data :value :data
                       (->> (mapv :data)))
          variants-status (mapv derive-render-status variants)
          failed-status (keep #{:failed} variants-status)]
      (cond
        (seq failed-status)
        [(count failed-status) :failed]

        (every? #{:succeeded} variants-status)
        [nil :succeeded]))))

(defn filter-renders [pred variant-renders]
  (->> (keep (fn [[variant-id renders]]
               (let [filtered (filter pred renders)]
                 (when (seq filtered) [variant-id filtered])))
             variant-renders)
       (into {})))

(rf/reg-sub :table.content-variants/succeeded-renders
  (fn [[_ variant-ids]]
    (rf/subscribe [:table.content-variants/renders variant-ids]))
  (fn [variant-renders _]
    (filter-renders #(= "succeeded" (:job-state %)) variant-renders)))

(rf/reg-sub :table.content-variants/pending-renders
  (fn [[_ variant-ids]]
    (rf/subscribe [:table.content-variants/renders variant-ids]))
  (fn [variant-renders _]
    (filter-renders #(contains? #{"created" "ready" "leased"} (:job-state %))
                    variant-renders)))

(rf/reg-sub :table.content-variants/failed-renders
  (fn [[_ variant-ids]]
    (rf/subscribe [:table.content-variants/renders variant-ids]))
  (fn [variant-renders _]
    (filter-renders #(= "failed" (:job-state %)) variant-renders)))

(rf/reg-sub :table.content-variants/bulk-actions
  :<- [:resource.content-variants/states]
  (fn [states]
    (let [status-counts (set/rename-keys states {:num-of-variants-in-approved :approved
                                                 :num-of-variants-in-draft :draft
                                                 :num-of-variants-in-published :published})]
      (mapcat (fn [[status count]]
                (let [bulk-actions #{:approve :unapprove :publish :unpublish}
                      actions (->> (status->allowed-actions status)
                                   (filter #(contains? bulk-actions %)))]
                  (map #(vector % count) actions))) status-counts))))

(rf/reg-sub :table.content-variants/total-variant-count
  :<- [:resource.content-variants/all]
  (fn [{:keys [value]}]
    (:total value)))

(rf/reg-sub :table.content-variants/has-next-page?
  :<- [:table.content-variants/total-variant-count]
  :<- [:table.content-variants/page]
  (fn [[total {page-size :size}] [_ page]]
    (let [seen (* page-size (inc page))]
      (< seen total))))
